/**
 * photometry — ONE light-unit table and the exposure CURVE (2026-09-25,
 * realism report §8 R1–R3 P0/P1, §10 R2/R9). Pure: no renderer state, the
 * `verify:lighting-physical` harness pins every rule here.
 *
 * WHY. Atlas lit the world with four unrelated knobs (the sky's
 * `sunIlluminance` 2.0, a key light lerped between four authored bands, the
 * clouds' 5.0, a fixed hemisphere) through a FIXED exposure 0.85: a lit ground
 * under a starry sky at tod 0.8, a sunrise at 50 % of noon, storms with a
 * full-intensity sharp sun, and an auto exposure nobody could turn on (it
 * would lift night to grey day). Now every light this layer owns is specified
 * in LUX against one table, and one exposure curve maps it to the screen.
 *
 * THE MODEL (all illuminances horizontal unless stated):
 *   key (normal)   E_key = E0 · T_atm(el) · fade(el) · T_cloud · (1 − sunDim)
 *                  T_atm is the engine's own CPU transmittance toward the sun
 *                  (its RGB IS the key colour); fade 0 → 1 between −1° and +1°.
 *   clear sky      E_sky = E0 · skyIrradiance(el)   (the Hillaire LUT maths —
 *                  lib/sky/sky_sampler; ≈ 0.168·E0 = 20 klx at high sun, the
 *                  HDRP "blue sky" value)
 *   overcast       E_oc  = OVERCAST_GLOBAL_FRACTION · (clear-sky global)
 *   diffuse        E_dif = mix(E_sky, E_oc, cloudiness)
 *   night floor    NIGHT_FLOOR_LUX (a moonlit average until the moon lands)
 *
 * THE EXPOSURE. EV100 of the scene's key (an 18 % card under the total
 * horizontal illuminance, K = 12.5) is `evScene`; a photographic exposure of
 * `evScene` would show EVERY scene at the same brightness — noon and midnight
 * alike. The "game night compensation" keeps part of the difference on
 * screen: the displayed key drops DISPLAY_DROP_PER_EV stops per stop of scene
 * darkening below a clear noon, capped at DISPLAY_DROP_MAX (so a night reads
 * ~5 stops under noon, not 17). `ev100 = evScene + drop` and the physical
 * exposure is `0.18 / 2^(ev100 − 3)` (L = 2^(EV−3) cd/m² at K 12.5).
 *
 * PRE-EXPOSURE (the fp16 law). The scene targets are half floats: physical
 * lux (a 120 klx sun, a 0.002 lux starlit sky) do not both fit. So every light
 * this layer owns is written PRE-EXPOSED: scene units = lux × `luxToScene`,
 * with luxToScene = exposurePhysical(ev100) / EXPOSURE_REF, and the renderer's
 * `toneMappingExposure` stays at EXPOSURE_REF × (the metered correction) ×
 * (the player's compensation). The render targets then carry display-scaled
 * values at every hour (Frostbite/UE pre-exposure, with the CURVE standing in
 * for last frame's exposure). Content authored in fixed scene units (lamps,
 * emissives, fire, the night photo) keeps reading as it was tuned under
 * EXPOSURE_REF — `emissiveEvScale` puts it on the EV scale (§10 R9).
 */

// ── The table (lux, sources in the research note §5: HDRP physical light units, the EV table) ──
/** The sun's illuminance before the air (the key light's source — the report's E0). */
export const E0_LUX = 120_000;
/** Reference: clear blue-sky horizontal diffuse at a high sun (HDRP "blue sky" 20 klx). The sampler's own value is the one used. */
export const SKY_CLEAR_REF_LUX = 20_000;
/** Thick overcast: the global horizontal left, as a fraction of the clear-sky global at the same sun (a dark rain deck ~7–8 klx at noon). */
export const OVERCAST_GLOBAL_FRACTION = 0.07;
/** Direct sun left under a full storm deck — direct : diffuse ≈ 1 : 3, no distinct shadows. */
export const T_CLOUD_MIN = 0.03;
/** Full-moon illuminance and the moonless starlight + airglow floor (HDRP). */
export const MOON_FULL_LUX = 0.25;
export const STARLIGHT_LUX = 0.002;
/**
 * The night ambient floor with NO moon in the sky (a dark-adapted eye's night floor — the game's, not the sky's: a
 * physically moonless 0.002 lux would leave the ground's display where it is (the curve's drop is capped below
 * ~0.3 lux) but put every lamp pool and fire light 4.6 stops further over the key). THE MOON (2026-09-27, sky/post
 * P2, realism-additions §1.5) now carries the night when it is up: the floor is `nightFloorLux(moonGlobal)` —
 * the moonless STARLIGHT_LUX plus whatever of this floor the moon's own light does not already cover.
 */
export const NIGHT_FLOOR_LUX = 0.05;
/** Street lighting on the pavement (tens of lux). */
export const LAMP_PAVEMENT_LUX = 20;
/** Ground albedo for the hemisphere's bounce colour. */
export const GROUND_ALBEDO = 0.2;

/** The renderer exposure every fixed-unit asset was tuned under (the old fixed 0.85). */
export const EXPOSURE_REF = 0.85;
/** Displayed-key drop per stop of scene darkening below a clear noon, and its cap. */
export const DISPLAY_DROP_PER_EV = 0.3;
export const DISPLAY_DROP_MAX = 5.5;
/** The clear-noon scene EV the drop is measured from (an equinox noon at the continent's centre, ≈ 95 klx global). */
export const EV_DAY = 15.4;
/** The metered correction's clamp (stops) around the curve — the BRIGHTENING range (caves, interiors, deep shade). */
export const AE_RANGE_EV = 1.5;
/**
 * The metered correction's DARKENING limit (stops), 2026-09-26: the honest meter reads the sky and the haze with the
 * ground — a clear-noon landscape meters ~2 stops over its own sunlit ground (the bright horizon ring) and a storm
 * ~0.6 under it, so darkening by the reading would dim every clear view and a lift by it every storm. The curve owns
 * the outdoor exposure; the meter brightens the dark and darkens the bright by at most this.
 */
export const AE_DARKEN_EV = 0.3;

/** The photographic EV table (Wikipedia "Exposure value", ISO 100) — the harness checks the curve lands in it. */
export const EV_TABLE = {
  clearNoon: [14.5, 16],
  overcastNoon: [11, 13],
  // 2026-09-27 (the coordinator's review): the two twilight rows were the OLD sky's — its multiple scattering was
  // 4π too strong, and twilight is all multiple scattering. Real twilight photometry (Kasten / the USNO table):
  // sun +2° global ≈ 2–6 klx → an 18 % card meters EV 9.6–11.2; −5° (civil twilight) ≈ 10–40 lx → EV 2.2–4.2.
  sunset: [9, 12],
  afterSunset: [1.5, 5],
  fullMoon: [-4, -1.5],
} as const;

// ── Pure maths ──
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
export function smoothstep(e0: number, e1: number, v: number): number { const t = clamp01((v - e0) / (e1 - e0)); return t * t * (3 - 2 * t); }
export const lum709 = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/** EV100 of an 18 % card under `lux` horizontal illuminance (reflected-light meter, K = 12.5). */
export function ev100ForIlluminance(lux: number): number {
  const L = (0.18 * Math.max(1e-6, lux)) / Math.PI; // cd/m²
  return Math.log2((L * 100) / 12.5);
}
/**
 * THE MOONLIT LIFT (sky/post P2, 2026-09-27 — THE MOON). The drop's cap flattened EVERY night to the same display
 * (−5.5 stops is reached at scene EV −2.9, above any night): a full moon (0.2 lux, EV −3.4) and the moonless floor
 * (0.05 lux, EV −5.45) showed the same near-black ground (the probe's full-moon midnight: mean 1.0 — its shadows
 * could not read). The cap now eases from DISPLAY_DROP_MAX at the moonless floor to DISPLAY_DROP_MAX − NIGHT_DISPLAY_LIFT
 * at a full moon's EV: a moonlit night reads 1.5 stops brighter than a moonless one (the eye is mesopic, not scotopic,
 * under a full moon — the Purkinje term keeps it blue-grey), the new-moon night stays exactly where it was, and the
 * blue hour (EV ~3, drop 3.7) is untouched (the plateau ends at EV 2).
 */
export const NIGHT_DISPLAY_LIFT = 1.5;
/** The scene EV of the moonless floor and of a full-moon night (0.2 lux) — the lift's ramp. */
export const EV_NIGHT_FLOOR = ev100ForIlluminance(NIGHT_FLOOR_LUX);
export const EV_MOONLIT = ev100ForIlluminance(0.2);
/** Stops the displayed key sits below a clear noon (the game-night compensation). */
export function displayDropStops(evScene: number): number {
  // A LINEAR ramp (2026-10-06): the curve's EV is evScene + cap, so the ramp's slope must stay under 1 per stop or the
  // camera runs BACKWARDS as the scene darkens. A smoothstep's steepest slope is 1.5/2 stops × 1.5 = 1.125 — the
  // exposure dipped 0.04 EV between 0.2 and 0.05 lux, unseen until THE LOCAL SUN gave deep twilight a sky light that
  // crosses that range (lighting-physical's monotone check). Linear: slope 0.75, the curve 0.25; both ends unchanged.
  const cap = DISPLAY_DROP_MAX - NIGHT_DISPLAY_LIFT * clamp01((evScene - EV_NIGHT_FLOOR) / (EV_MOONLIT - EV_NIGHT_FLOOR));
  return Math.min(cap, Math.max(0, (EV_DAY - evScene) * DISPLAY_DROP_PER_EV));
}
/**
 * THE HEADSET NIGHT (2026-09-28, the Quest 3 "black screen"). The curve keeps a moonless night 5.5 stops under noon —
 * a dark night on a monitor, where the post chain and the room's light carry it; measured the same on both paths (the
 * desktop frame 15/255, the VR eye buffer 12/255 at midnight). Inside a headset there is no room light and the Quest 3's
 * LCD shows 12/255 as black (the eye-buffer screenshot from the device). In a headset the displayed drop is capped at
 * XR_DISPLAY_DROP_MAX: the stops beyond it are given back as exposure. Zero in daylight and at the blue hour's start;
 * a full moon ≈ 1 stop, a moonless midnight 2.5.
 */
export const XR_DISPLAY_DROP_MAX = 3;
export function xrNightLiftEv(dropStops: number): number {
  return Number.isFinite(dropStops) ? Math.max(0, dropStops - XR_DISPLAY_DROP_MAX) : 0;
}
/** The exposure CURVE: the EV100 the camera is set to for a scene of `evScene`. */
export function evTarget(evScene: number): number { return evScene + displayDropStops(evScene); }
/** Physical exposure for an EV100 (multiplies cd/m²). */
export function exposurePhysical(ev100: number): number { return 0.18 / Math.pow(2, ev100 - 3); }
/** Scene units per lux at an EV100 (the pre-exposure every owned light is written with). */
export function luxToScene(ev100: number): number { return exposurePhysical(ev100) / EXPOSURE_REF; }

/** Sun fade across the horizon: 0 below −1°, 1 above +1° (the disc's own size + refraction). */
export function horizonFade(sinEl: number): number {
  return smoothstep(Math.sin(-1 * Math.PI / 180), Math.sin(1 * Math.PI / 180), sinEl);
}
/** How overcast the sky reads 0..1: none until the cover passes 0.55, full at 0.95 × a dense deck. */
export function cloudiness(cover: number, density: number): number {
  return smoothstep(0.55, 0.95, cover) * clamp01(density);
}
/** Direct-sun transmittance through the deck (the report's T_cloud). */
export function tCloud(c: number): number { return 1 + (T_CLOUD_MIN - 1) * clamp01(c); }

export interface LightInputs {
  /** sin of the sun's elevation. */
  sinEl: number;
  /** Atmospheric transmittance toward the sun at the observer (RGB). */
  tAtm: { x: number; y: number; z: number };
  /** Clear-sky horizontal irradiance per unit E0 (RGB) — lib/sky/sky_sampler. */
  skyIrr: { x: number; y: number; z: number };
  /** Weather: cloud cover 0..1, deck density, dust dimming 0..1. */
  cover: number; density: number; sunDim: number;
  /** Extra night illuminance (a moon) in lux, default the floor. */
  nightLux?: number;
}
export interface LightModel {
  /** Key light: colour (max-normalised linear RGB) and NORMAL illuminance in lux. */
  keyColor: [number, number, number]; keyLux: number;
  /** Diffuse sky (clear or overcast) horizontal lux + its colour (normalised). */
  diffuseLux: number; diffuseColor: [number, number, number];
  /** Horizontal global = direct·sinEl + diffuse + night. */
  globalLux: number;
  /** The clear-sky global and the full-overcast diffuse at this sun (what a CIE overcast dome is scaled to). */
  clearGlobalLux: number; overcastLux: number;
  cloudiness: number; tCloud: number; fade: number;
  evScene: number; ev100: number; drop: number;
  /** Scene units per lux (pre-exposure) at ev100. */
  luxToScene: number;
}

/** The light at one moment — the rule every consumer shares. */
export function lightModel(i: LightInputs): LightModel {
  const fade = horizonFade(i.sinEl);
  const c = cloudiness(i.cover, i.density);
  const tc = tCloud(c);
  const dim = 1 - clamp01(i.sunDim);
  const tl = Math.max(1e-9, Math.max(i.tAtm.x, i.tAtm.y, i.tAtm.z));
  const keyColor: [number, number, number] = [i.tAtm.x / tl, i.tAtm.y / tl, i.tAtm.z / tl];
  const keyLux = E0_LUX * tl * fade * tc * dim;
  const keyLumLux = keyLux * lum709(keyColor[0], keyColor[1], keyColor[2]);
  const sinUp = Math.max(0, i.sinEl);
  const skyLum = lum709(i.skyIrr.x, i.skyIrr.y, i.skyIrr.z);
  const clearSkyLux = E0_LUX * skyLum;
  const clearGlobal = E0_LUX * lum709(i.tAtm.x, i.tAtm.y, i.tAtm.z) * fade * sinUp + clearSkyLux;
  const overcastLux = OVERCAST_GLOBAL_FRACTION * clearGlobal;
  const diffuseLux = clearSkyLux + (overcastLux - clearSkyLux) * c;
  // Diffuse colour: the sky's own hue, greying toward neutral under the deck.
  const sl = Math.max(1e-9, Math.max(i.skyIrr.x, i.skyIrr.y, i.skyIrr.z));
  const sk: [number, number, number] = skyLum > 1e-12 ? [i.skyIrr.x / sl, i.skyIrr.y / sl, i.skyIrr.z / sl] : [0.55, 0.62, 0.8];
  const grey: [number, number, number] = [0.86, 0.9, 0.96];
  const diffuseColor: [number, number, number] = [sk[0] + (grey[0] - sk[0]) * c, sk[1] + (grey[1] - sk[1]) * c, sk[2] + (grey[2] - sk[2]) * c];
  const night = i.nightLux ?? NIGHT_FLOOR_LUX;
  const globalLux = keyLumLux * sinUp + diffuseLux + night;
  const evScene = ev100ForIlluminance(globalLux);
  const drop = displayDropStops(evScene);
  const ev100 = evScene + drop;
  return { keyColor, keyLux, diffuseLux, diffuseColor, globalLux, clearGlobalLux: clearGlobal, overcastLux, cloudiness: c, tCloud: tc, fade, evScene, ev100, drop, luxToScene: luxToScene(ev100) };
}

// ── The live exposure state (LightingModule writes it, PostModule applies the metering, everyone reads) ──
export interface ExposureState {
  /** 'physical' = the curve (+ metering when on); 'fixed' = the player's own exposure, no curve. */
  mode: 'physical' | 'fixed';
  /** The curve's EV100 and the scene's own EV100 (the drop between them is the night compensation). */
  ev100: number; evScene: number;
  /** Scene units per lux this frame (the pre-exposure every owned light uses). */
  luxToScene: number;
  /** The renderer's toneMappingExposure right now (EXPOSURE_REF × correction × compensation). */
  exposure: number;
  exposureRef: number;
  /** The player's exposure compensation multiplier (the old slider / 0.85). */
  compensation: number;
  /** The metered correction in stops (±AE_RANGE_EV), 0 when metering is off. */
  correctionEv: number;
  /** Stops given back to a headset night (xrNightLiftEv), 0 off the headset. */
  xrLiftEv?: number;
  metering: 'off' | 'average' | 'histogram';
  /** The meter's last reading (scene-target luminance, pre-exposed units) and the key it aimed at — the bias calibration's inputs. */
  meteredLum?: number;
  meterKey?: number;
  /** The darkening limit the metering runs with (AE_DARKEN_EV). */
  darkenEv?: number;
}
export const exposureState: ExposureState = {
  mode: 'physical', ev100: EV_DAY, evScene: EV_DAY, luxToScene: luxToScene(EV_DAY),
  exposure: EXPOSURE_REF, exposureRef: EXPOSURE_REF, compensation: 1, correctionEv: 0, metering: 'off',
};

/**
 * EMISSIVES IN EV (§10 R9): the scale an emissive authored in fixed scene
 * units takes so it reads `evRel` stops over the reference, whatever the
 * metered correction does — `2^evRel × exposureRef / exposure`. Suggested
 * classes: screens +1, neon / bio-glass +3, lamps +5.
 */
export function emissiveEvScale(evRel: number, state: Pick<ExposureState, 'exposure' | 'exposureRef'> = exposureState): number {
  return Math.pow(2, evRel) * state.exposureRef / Math.max(1e-6, state.exposure);
}

// ── THE PHYSICAL-EMISSIVE CONTRACT (lighting session 3, 2026-09-26 — the coordinator's QA finding 4) ──
// Noon and midnight differ by ~15 stops of `luxToScene`: anything self-lit or sun-lit that is written in fixed
// scene units is off the curve (a fire core that clips white at noon and is lost at night; impostors that brighten
// at sunset; water on the old sun scale). Four entry points, one per kind of thing — write the result into a uniform
// EVERY FRAME (these move with the hour, the weather and the metering), never bake it:
//   emissiveNits(nits)      a surface that EMITS light, by its luminance in cd/m² (EMISSIVE_NITS has references)
//   luxToScene × candela    a light SOURCE you add (three's PointLight/SpotLight intensity is candela; I/d² is lux)
//   sunSceneLight() + skySceneIrradiance()   a custom shader LIT by the sun and the sky (water, impostors, cards)
//   emissiveEvScale(evRel)  a thing whose SCREEN brightness must not follow the hour (a readable screen, a hologram)

/** Reference luminances (cd/m², "nits") for self-lit surfaces — tune within the range, never replace with a scene constant. */
export const EMISSIVE_NITS = {
  /** A lit window seen from outside (a room at ~200 lux behind glass). */
  window: 120,
  /** A backlit screen / terminal. */
  screen: 250,
  /** Glowing embers, lava's crust glow. */
  embers: 600,
  /** Open lava. */
  lava: 1500,
  /** A neon / bio-glass tube. */
  neon: 1500,
  /** A diffuse lamp globe. */
  lampGlobe: 2500,
  /** Wood-fire flames (a candle's flame is ~10⁴). */
  flame: 4000,
  /** A bare bulb / the hot core of a flame. */
  lampBulb: 20000,
} as const;
/**
 * The most a self-lit surface may sit above the scene's key on screen, in stops. Physically a 4,000-nit fire is ~20
 * stops over a moonlit key (0.003 cd/m²) — the display holds ~7 and the threshold-less bloom would spread the rest
 * over the frame. 10 stops keeps a night fire a white-hot glare with a halo and leaves it untouched by day (the day's
 * key is ~5,400 cd/m², so no reference above ever meets the cap before dusk).
 */
export const EMISSIVE_MAX_OVER_KEY_EV = 10;
/** The scene's key in cd/m²: an 18 % card under the global horizontal light (the curve's evScene). */
export function keyLuminanceNits(state: Pick<ExposureState, 'evScene'> = exposureState): number {
  return Math.pow(2, state.evScene - 3);
}
/**
 * Scene radiance for a self-lit surface of `nits` cd/m² (multiply the emissive colour by it, every frame): the
 * luminance × `luxToScene`, capped at `maxOverKeyEv` stops over the key (EMISSIVE_MAX_OVER_KEY_EV). A 4,000-nit
 * flame is then ~0.7× the key at noon (a fire in daylight is pale — it does not clip) and a white-hot glare at night.
 */
export function emissiveNits(nits: number, maxOverKeyEv = EMISSIVE_MAX_OVER_KEY_EV, state: Pick<ExposureState, 'evScene' | 'luxToScene'> = exposureState): number {
  const cap = keyLuminanceNits(state) * Math.pow(2, maxOverKeyEv);
  return Math.min(Math.max(0, nits), cap) * state.luxToScene;
}
/** The key light (the sun through the air and the cloud deck) as an ILLUMINANCE in scene units, RGB — the DirectionalLight's own colour × intensity (LightingModule writes it). 0 below the horizon. */
export function sunSceneLight(): Rgb { return lightFrame.keyScene; }
/** The whole sky's horizontal irradiance in scene units, RGB (the diffuse sky + the night floor — NOT the hemisphere light's 12 % floor under the IBL). For a shader that does not read scene.environment. */
export function skySceneIrradiance(): Rgb { return lightFrame.skyScene; }

/** A metered correction (stops) toward `key`, clamped around the curve — the "never free-running" rule. */
export function meteredCorrectionEv(meteredSceneLum: number, curveExposure: number, key: number, range = AE_RANGE_EV, darken = range): number {
  const displayed = Math.max(1e-6, meteredSceneLum * curveExposure);
  const want = Math.log2(key / displayed);
  return Math.max(-darken, Math.min(range, want));
}

/**
 * THE LIGHT THIS FRAME — LightingModule (RenderSub/4) writes it, the sky (5),
 * the clouds + weather (22), the fog (Atmosphere, 1 Hz) and the post inputs
 * read it, so every consumer lights from the same numbers. Radiances are per
 * unit E0 (multiply by E0_LUX · luxToScene for scene units).
 */
export interface Rgb { x: number; y: number; z: number }
export const lightFrame = {
  valid: false,
  model: null as LightModel | null,
  sinEl: 1,
  /** The sky (per unit E0): the upper hemisphere's mean radiance, zenith, horizon ring, toward / away from the sun. */
  skyMean: { x: 0, y: 0, z: 0 } as Rgb,
  zenith: { x: 0, y: 0, z: 0 } as Rgb,
  horizon: { x: 0, y: 0, z: 0 } as Rgb,
  horizonSun: { x: 0, y: 0, z: 0 } as Rgb,
  /** Transmittance toward the sun at the cloud base altitude (the clouds' sun colour, RGB 0..1). */
  cloudSunT: { x: 1, y: 1, z: 1 } as Rgb,
  /** The same at the HIGH deck's altitude (cloud_light's CLOUD_HIGH_KM — the 3.8–9.6 km ray-march deck). */
  cloudSunTHigh: { x: 1, y: 1, z: 1 } as Rgb,
  /** Weather the model saw. */
  cover: 0, density: 1, sunDim: 0, precip: 0,
  /** Has the sky an IBL (scene.environment) to carry the diffuse? */
  ibl: false,
  /**
   * THE BAKED NIGHT (2026-10-06): the night's ambient irradiance (lux) the IBL cube carries — (1 − HEMI_IBL_FRACTION) ×
   * (the floor + the town's glow) × the night factor, written by LightingModule (which takes it OUT of the hemisphere),
   * read by SkyModule (which bakes it into the cube, shaped as the drawn night sky) so shiny surfaces reflect the night.
   */
  nightIblLux: 0,
  /**
   * THE LIVE NIGHT (2026-10-06): the night lux the LIVE cube delivers (its baked term × the carrier's intensity, at this
   * frame's exposure) — SkyModule writes it as each cube lands (lib/sky/night_ibl) — and what LightingModule actually took
   * out of the hemisphere (min(live, the share it has to give)), so the moved ambient follows the cube in time.
   */
  nightIblLive: 0,
  nightIblApplied: 0,
  /** The key light's illuminance in scene units, RGB (the DirectionalLight's colour × intensity) — sunSceneLight(). */
  keyScene: { x: 0, y: 0, z: 0 } as Rgb,
  /** The whole sky's horizontal irradiance in scene units, RGB — skySceneIrradiance(). */
  skyScene: { x: 0, y: 0, z: 0 } as Rgb,
  // sky/post (2026-09-27, THE MOON): the key is the BRIGHTER of the sun and the moon — keyScene above is that key's
  // light and `keyDir` its unit direction (the sun by day, the moon at night; a water road / a soot lobe follows it);
  // `sunDir` is always the sun's; `nightLux` the floor + the moon's global (the ambient any night shader lifts to).
  keyDir: { x: 0, y: 1, z: 0 } as Rgb,
  keyIsMoon: false,
  sunDir: { x: 0, y: 1, z: 0 } as Rgb,
  nightLux: NIGHT_FLOOR_LUX,
  /** The moon this frame (null before LightingModule's first frame). */
  moon: null as null | { dir: Rgb; sinEl: number; alphaDeg: number; phase: number; illum: number; model: MoonModel; keyScene: Rgb; floorLux: number },
};

// ── THE MOON (2026-09-27, sky/post P2 — realism-additions §1.5) ─────────────────────────────────────────────────
// Moonlight is SUNLIGHT reflected off the regolith: the same air, the same deck and the same sky maths as the key,
// with the moon's own E0 — MOON_FULL_LUX (0.25 lux) is a full moon OVERHEAD after the air, so the top-of-air
// illuminance is that ÷ the zenith transmittance, times the PHASE LAW. The moonlit sky is the day sky's shape at the
// moon's elevation scaled by that E0 (Rayleigh does not care which star lights it: a full-moon sky is a very dark
// BLUE sky, ~2·10⁻⁶ of noon). `moonModel` is pure; LightingModule drives the `moonLight` DirectionalLight from it.

/** Moonlight's colour temperature — sunlight reddened by the regolith (~4100 K); the Purkinje post term makes it read blue-grey. */
export const MOON_COLOR_K = 4100;
/** The moon's mean angular diameter (degrees). */
export const MOON_DISC_DEG = 0.52;
/** A synodic month in WORLD days (one world day = one sky cycle): all eight phases over ~2.5 real days at the 2 h cycle. */
export const MOON_SYNODIC_DAYS = 29.53;
/** The full disc's luminance (cd/m²) at the top of the air: MOON_FULL_LUX / T_zenith over its solid angle ≈ 4,000 (measured 2,500–4,000). */
export const MOON_DISC_NITS = 4000;
/**
 * The most the disc may sit over the scene's key on screen (stops) — below the emissive cap (10): at 10 the whole
 * disc clips to white and the phase terminator and the maria vanish; at 7 a full moon is a white-hot disc whose
 * shading still reads. By day (a 5,400 cd/m² key) no cap applies — the daytime moon is its physical pale disc.
 */
export const MOON_DISC_MAX_OVER_KEY_EV = 7;
/**
 * The moon's colour RELATIVE TO THE KEY'S WHITE: the engine's top-of-air sun is (1, 1, 1) (the key's colour is the air's
 * transmittance alone), so the moon is the 4100 K / 5800 K blackbody ratio (lib/vfx/blackbody: (1, .669, .401) ÷
 * (1, .880, .826)) — the regolith's reddening, not an absolute 4100 K (which would be the sun's own yellow twice).
 */
export const MOON_CHROMA: [number, number, number] = [1, 0.76, 0.486];

/**
 * The phase law — the full moon's brightness at a phase angle α (deg, 0 = full) relative to full: Allen's
 * Δm = 0.026|α| + 4·10⁻⁹α⁴ (Krisciunas & Schaefer 1991). The opposition surge makes it steep: a quarter moon
 * (α 90°) is 0.09 of full, not half.
 */
export function moonPhaseLaw(alphaDeg: number): number {
  const a = Math.min(180, Math.abs(alphaDeg));
  return Math.pow(10, -0.4 * (0.026 * a + 4e-9 * a * a * a * a));
}
/** The lit fraction of the disc at a phase angle α (deg): (1 + cos α) / 2. */
export function moonIllumFraction(alphaDeg: number): number { return (1 + Math.cos((alphaDeg * Math.PI) / 180)) / 2; }
/**
 * The night floor under a moon whose own global (direct horizontal + its sky, lux) is `moonGlobalLux`: the moonless
 * STARLIGHT_LUX plus the part of NIGHT_FLOOR_LUX the moon does not cover — a new moon keeps today's floor, a full moon
 * high in a clear sky takes the floor down to starlight and carries the night itself (`floor + moon ≥ NIGHT_FLOOR_LUX`).
 */
export function nightFloorLux(moonGlobalLux: number): number {
  return STARLIGHT_LUX + Math.max(0, NIGHT_FLOOR_LUX - STARLIGHT_LUX - Math.max(0, moonGlobalLux));
}
export interface MoonInputs {
  /** sin of the moon's elevation. */
  sinEl: number;
  /** Transmittance toward the moon (RGB) and toward the zenith (the E0 reference). */
  tAtm: { x: number; y: number; z: number };
  tAtmZenith: { x: number; y: number; z: number };
  /** Clear-sky horizontal irradiance per unit E0 at the moon's elevation (lib/sky/sky_sampler). */
  skyIrr: { x: number; y: number; z: number };
  /** The phase angle α (deg, 0 = full, 180 = new). */
  alphaDeg: number;
  /** The weather the key sees (photometry's cloudiness / tCloud / dust dim). */
  cloudiness: number; tCloud: number; sunDim: number;
}
export interface MoonModel {
  /** The moon's top-of-air illuminance at this phase (lux, luminous). */
  e0Lux: number;
  /** Key: max-normalised colour and NORMAL illuminance in lux (the same convention as the sun's keyLux). */
  keyColor: [number, number, number]; keyLux: number;
  /** Luminous normal illuminance (keyLux × lum(keyColor)). */
  keyLumLux: number;
  /** The moonlit sky: horizontal lux (clear → overcast) and its colour (max-normalised). */
  diffuseLux: number; diffuseColor: [number, number, number];
  /** Direct horizontal + diffuse (lux) — what the night floor subtracts. */
  globalLux: number;
  fade: number; phaseLaw: number;
}
/** The moon's light at one moment (pure — the harness pins it). */
export function moonModel(i: MoonInputs): MoonModel {
  const phaseLaw = moonPhaseLaw(i.alphaDeg);
  const cl = lum709(MOON_CHROMA[0], MOON_CHROMA[1], MOON_CHROMA[2]);
  const tz = Math.max(1e-6, lum709(i.tAtmZenith.x, i.tAtmZenith.y, i.tAtmZenith.z));
  const e0Lux = (MOON_FULL_LUX / tz) * phaseLaw;
  const fade = horizonFade(i.sinEl);
  const c = clamp01(i.cloudiness);
  const dim = 1 - clamp01(i.sunDim);
  // key: the 4100 K spectrum through the air toward the moon, faded across the horizon, dimmed by the deck and dust
  const kr = MOON_CHROMA[0] * i.tAtm.x, kg = MOON_CHROMA[1] * i.tAtm.y, kb = MOON_CHROMA[2] * i.tAtm.z;
  const km = Math.max(1e-9, kr, kg, kb);
  const keyColor: [number, number, number] = [kr / km, kg / km, kb / km];
  const keyLux = (e0Lux / cl) * km * fade * i.tCloud * dim;
  const keyLumLux = keyLux * lum709(keyColor[0], keyColor[1], keyColor[2]);
  // sky: the day sky's irradiance at the moon's elevation, in the moon's spectrum, greying toward the overcast share
  const sr = MOON_CHROMA[0] * i.skyIrr.x, sg = MOON_CHROMA[1] * i.skyIrr.y, sb = MOON_CHROMA[2] * i.skyIrr.z;
  const clearSky = (e0Lux / cl) * lum709(sr, sg, sb);
  const sinUp = Math.max(0, i.sinEl);
  const clearDirect = (e0Lux / cl) * lum709(kr, kg, kb) * fade * sinUp;
  const overcast = OVERCAST_GLOBAL_FRACTION * (clearDirect + clearSky);
  const diffuseLux = (clearSky + (overcast - clearSky) * c) * dim;
  const sm = Math.max(1e-9, sr, sg, sb);
  const sk: [number, number, number] = clearSky > 1e-12 ? [sr / sm, sg / sm, sb / sm] : [0.55, 0.62, 0.8];
  const grey: [number, number, number] = [0.86, 0.9, 0.96];
  const diffuseColor: [number, number, number] = [sk[0] + (grey[0] - sk[0]) * c, sk[1] + (grey[1] - sk[1]) * c, sk[2] + (grey[2] - sk[2]) * c];
  const globalLux = keyLumLux * sinUp + diffuseLux;
  return { e0Lux, keyColor, keyLux, keyLumLux, diffuseLux, diffuseColor, globalLux, fade, phaseLaw };
}

/** CIE overcast zenith radiance for a horizontal illuminance (L(θ) = Lz(1 + 2cosθ)/3 ⇒ E = 7π/9 · Lz). */
export function overcastZenithRadiance(horizontalLux: number): number { return (horizontalLux * 9) / (7 * Math.PI); }
